import {
    useState, useMemo, useCallback,
} from 'react'
import moment from 'moment/moment'
import kebabCase from 'lodash/kebabCase'
import {
    useDispatch,
} from 'react-redux'
import downloadDocument from 'app/utils/downloadDocument'
import SkyNetStepper from 'app/shared-components/SkyNetStepper'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import useBeforeUnloadDialog from 'app/hooks/useBeforeUnloadDialog'
import useAccountManagementRoutes from 'app/SkyNetRoutes/hooks/useAccountManagementRoutes'
import {
    successedToSaveForm,
} from 'app/shared-components/FormStatuses/store/formStatuses.slice'

import {
    CQuoteInformationContext, TCQuoteStepper,
} from './CQuoteInformationContext'
import CQuoteSelectLane from './Steps/CQuoteSelectLane'
import CQuoteLaneDetails from './Steps/CQuoteLaneDetails'
import CQuoteDeliveryOptions from './Steps/CQuoteDeliveryOptions'
import CQuoteOfferSetup from './Steps/CQuoteOfferSetup'
import CQuoteFeesContactDetails from './Steps/CQuoteFeesContactDetails'
import useCQuoteCreate from './hooks/useCQuoteCreate'
import useCQuoteGeneratePDF from './hooks/useCQuoteGeneratePDF'

export default function CQuoteCreateStepper() {
    const dispatch = useDispatch()
    const [
        value,
        setValue,
    ] = useState<TCQuoteStepper>({
        lane: {},
        laneDetails: {},
        deliveryOptions: {},
        contactDetails: {},
    } as TCQuoteStepper)
    const {
        CQuotes: CQuotesRoutes,
    } = useAccountManagementRoutes()

    const onSuccess = useCallback(({
        id,
    }: { id: number }) => {
        CQuotesRoutes.Overview.navigate({
            params: {
                key: id,
            },
        })
    }, [CQuotesRoutes.Overview])

    const onGeneratePDFSuccess = useCallback((responseData, locationName) => {
        const fileName = `${moment().format('YYYYMMDD')}${locationName ? `_${kebabCase(locationName)}` : ''}`

        downloadDocument(responseData, fileName)

        dispatch(
            successedToSaveForm({
                id: 'Notification',
                title: 'The C-Quote have been successfully downloaded.',
            }),
        )
    }, [dispatch])

    const createCQuoteHandler = useCQuoteCreate({
        onSuccess,
    })
    const generateCQuotePDFHandler = useCQuoteGeneratePDF({
        onSuccess: onGeneratePDFSuccess,
    })

    const isEdited = Boolean(value?.lane?.id || value?.laneDetails)
        && !(createCQuoteHandler.isPending || createCQuoteHandler.isSuccess)
        && !(generateCQuotePDFHandler.isPending || generateCQuotePDFHandler.isSuccess)

    useBeforeUnloadDialog(isEdited)

    const [
        activeStep,
        setActiveStep,
    ] = useState<number>(0)

    const stepperSteps = useMemo(() => {
        return [
            {
                key: '1',
                label: 'Select Lane',
                expanded: true,
                renderContent: ({
                    index,
                }: Readonly<{ index: number }>) => {
                    return <CQuoteSelectLane index={index} />
                },
            },
            {
                key: '2',
                label: 'Lane Details',
                expanded: (step: number) => {
                    return step > 0
                },
                renderContent: ({
                    index,
                }: Readonly<{ index: number }>) => {
                    return <CQuoteLaneDetails index={index} />
                },
            },
            {
                key: '3',
                label: 'Delivery Options',
                expanded: (step: number) => {
                    return step > 1
                },
                renderContent: ({
                    index,
                }: Readonly<{ index: number }>) => {
                    return <CQuoteDeliveryOptions index={index} />
                },
            },
            {
                key: '4',
                label: 'Offer Setup',
                expanded: (step: number) => {
                    return step > 2
                },
                renderContent: ({
                    index,
                }: Readonly<{ index: number }>) => {
                    return <CQuoteOfferSetup index={index} />
                },
            },
            {
                key: '5',
                label: 'Fees and Contact Details',
                expanded: (step: number) => {
                    return step > 3
                },
                renderContent: ({
                    index,
                }: Readonly<{ index: number }>) => {
                    return (
                        <CQuoteFeesContactDetails
                            index={index}
                            onCreate={createCQuoteHandler}
                            onGeneratePDF={generateCQuotePDFHandler}
                        />
                    )
                },
            },
        ]
    }, [
        createCQuoteHandler,
        generateCQuotePDFHandler,
    ])

    const cQuoteInformationContext = useMemo(() => {
        return {
            activeStep,
            setActiveStep,
            value,
            setValue,
        }
    }, [
        activeStep,
        value,
        setValue,
    ])

    return (
        <CQuoteInformationContext.Provider value={cQuoteInformationContext}>
            <SkyNetStepper
                activeStep={activeStep}
                steps={stepperSteps}
            />
            <HistoryBlockPrompt
                when={isEdited}
                positiveLabel="Discard"
                isPositiveAlert
                promptText="You are about to leave this page, your progress will be lost. Would you like to discard your changes anyway?"
            />
        </CQuoteInformationContext.Provider>
    )
}
